import React, { createContext, useContext, useEffect, useState } from "react";
import { onIdTokenChanged, signOut } from "firebase/auth";
import { Typography } from "@mui/material";
import api from "../services/api";
import { auth } from "../firebase";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [firebaseInitializing, setFirebaseInitializing] = useState(true);

  // Token listener
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          setIsAuthenticated(true);
          await fetchUserDetails();
        } catch (err) {
          console.error("🔥 Failed to get ID token or user details:", err);
          await handleLogout();
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false); // ✅ add this here to prevent lock
      }

      setFirebaseInitializing(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          await user.getIdToken(true); // force-refresh token
          console.log("🔁 Token proactively refreshed");
        } catch (err) {
          console.warn("⚠️ Background token refresh failed", err);
        }
      }
    }, 45 * 60 * 1000); // every 45 minutes

    return () => clearInterval(interval);
  }, []);

  const fetchUserDetails = async (idTokenOverride = null) => {
    try {
      const token =
        idTokenOverride ?? (await auth.currentUser?.getIdToken(true));

      const { data } = await api.get("/users/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUser({
        profile: data.profile,
        firebaseUser: data.firebaseUser,
        employee: data.employee,
      });

      setIsAuthenticated(true);
    } catch (err) {
      console.error("🚫 Failed to fetch user details:", err);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    const firebaseUser = auth.currentUser;

    if (firebaseUser) {
      const idToken = await firebaseUser.getIdToken(true); // force-refresh token
      setIsAuthenticated(true);
      await fetchUserDetails(idToken); // pass explicitly
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
  };

  const shouldShowContent = !firebaseInitializing && !loading;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        handleLogin,
        handleLogout,
        fetchUserDetails,
        loading,
      }}>
      {shouldShowContent ? (
        children
      ) : (
        <Typography>Loading user data...</Typography>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
